// Side-effect module: `Amplify.configure(amplify_outputs.json)` runs at
// module-eval time. Importing this module (even via `import "./amplify-init"`)
// is what wires the SDK; importing `isPlaceholderConfig` is the bonus.
//
// Why this lives in its own module instead of in `index.tsx`:
//
//   index.tsx used to do
//     import App from "./App";              // line 3 -- transitive imports
//                                           //   Apple.tsx -> DataContextProvider.tsx
//                                           //   -> `const client = generateClient()`
//                                           // run at MODULE-EVAL time, BEFORE
//                                           // index.tsx's body is reached.
//     ...
//     Amplify.configure(amplifyOutputs);    // ~line 72 -- runs in the body, AFTER
//                                           // every imported module has already
//                                           // been evaluated.
//
//   By the time Amplify.configure() ran, `client.models` had been built off an
//   empty Amplify singleton (`isConfigured = false`, `resourcesConfig = {}`).
//   The SDK has a `Hub.listen('core', ...)` mitigation that re-attaches a real
//   models proxy when configure later fires, but the SDK's own source comment
//   in `@aws-amplify/api-graphql/src/internals/generateClient.ts` warns:
//
//     "This happens when the `Amplify.configure()` call gets evaluated after
//      the `generateClient()` call. Cause: when the `generateClient()` and the
//      `Amplify.configure()` calls are located in different source files,
//      script bundlers may randomly arrange their orders in the production
//      bundle."
//
//   The mitigation worked in some local + dev-bundle shapes and silently
//   failed in the production bundle the operator tested on 2026-05-21
//   (MR !42, smoke regression note `3374166197`):
//
//     "Amplify has not been configured. Please call Amplify.configure() before
//      using this service."
//
//   followed by the cascading
//
//     "DataContextProvider.tsx:156 Cannot read properties of null (reading
//      'tags')"
//
//   when `client.models.ProdApplicant.list()` returned `{ data: null, errors:
//   [...] }` and `data.map(...)` then crashed.
//
// Fix shape: `Amplify.configure` is the side effect of importing this module,
// and `index.tsx` imports this module FIRST -- before `import App from
// "./App"` and therefore before any module that calls `generateClient()` at
// its own module-eval time. ESM's import-evaluation order guarantee makes the
// "configure before generateClient" invariant deterministic regardless of how
// the bundler orders later modules.
//
// On the placeholder path (fresh clone with no `.ampx-workdir/` outputs and no
// Amplify Hosting build to run `ampx generate outputs`), `_placeholder: true`
// causes us to skip the configure call altogether. `index.tsx` reads
// `isPlaceholderConfig` from this module and renders an explicit error UI
// instead of mounting `<App />`. The placeholder path was the previous
// `index.tsx` behavior; we preserve it here.
//
// Regression coverage: `viewer-client/src/__tests__/amplify-init-order.test.tsx`
// asserts that loading the production import chain (index.tsx) calls
// `Amplify.configure` strictly before any `generateClient()` call. The test
// would have caught the 2026-05-21 smoke failure before it shipped.
import { Amplify } from "aws-amplify";
import amplifyOutputs from "./amplify_outputs.json";

export const isPlaceholderConfig: boolean = !!(
  amplifyOutputs as { _placeholder?: boolean }
)._placeholder;

if (!isPlaceholderConfig) {
  // The JSON module's static type is inferred from the placeholder shape, so
  // cast to the runtime `Amplify.configure` parameter type. The shape is the
  // Amplify Gen 2 outputs format; `parseAmplifyConfig` inside the SDK
  // converts it to the internal ResourcesConfig shape.
  Amplify.configure(amplifyOutputs as Parameters<typeof Amplify.configure>[0]);
}
